/**
 * EconQuest canonical game-balance constants.
 * Mirrors EconQuest_Data_Model_v1.0.md §1.1 + §5 (config re-seed checklist).
 * These are the corrected values that supersede the original admin-doc defaults.
 */
export const GAME_BALANCE = {
    MAX_ATTEMPTS_PER_BATTLE: 3,
    /**
     * XP for clearing a DEFINITION/EXPLAIN battle (Phase 4.2 M4.5 — mark-value-scaled).
     * Base XP = XP_PER_MARK × mark_value. This replaces the flat XP_REGULAR_CLEAR=20
     * that used to apply uniformly regardless of mark_value (so a 12-mark Evaluate paid
     * the same as a 1-mark MCQ). XP_REGULAR_CLEAR is retained as a safety fallback only.
     */
    XP_PER_MARK: 10,
    /** Deprecated fallback — used only if mark_value is somehow null/undefined. */
    XP_REGULAR_CLEAR: 20,
    XP_MINIBOSS_CLEAR: 60,
    XP_STAGEBOSS_CLEAR: 180,
    XP_FINALBOSS_CLEAR: 420,
    XP_PORTAL_BONUS: 30,
    /** Bonus when a battle is cleared with full marks on the first attempt, no hint. */
    XP_PERFECT_STRIKE_BONUS: 15,
};
/**
 * Level curve (Phase 3). Cumulative XP required to REACH level N is
 * LEVEL_CURVE.cumulativeXpForLevel(N). Gentle quadratic: 100 * N*(N-1)/2.
 *   L1=0, L2=100, L3=300, L4=600, L5=1000, ...
 * level(totalXp) returns the highest N whose threshold is <= totalXp (min 1).
 */
export const LEVEL_CURVE = {
    cumulativeXpForLevel(n) {
        const lvl = Math.max(1, Math.floor(n));
        return (100 * (lvl * (lvl - 1))) / 2;
    },
    levelForXp(totalXp) {
        const xp = Math.max(0, totalXp);
        let n = 1;
        while (this.cumulativeXpForLevel(n + 1) <= xp)
            n += 1;
        return n;
    },
};
/** Stage-clear and boss-unlock gates (Data Model §1.3). */
export const PROGRESSION = {
    BOSS_UNLOCK_REGULAR_PCT: 0.8, // 80% regular battles cleared
    STAGE_CLEAR_MIN_SCORE: 5, // Stage Boss >= 5/9
    FINAL_BOSS_MIN_SCORE: 8, // >= 8/12
    WORLD2_MIN_W1_STAGES: 5, // >=5 W1 stages cleared AND W1 mini-boss
    // M9-patch2: tightened from 2 → 4. Final Boss now requires ALL permanent
    // portals deployed (≥1 cleared question on each of A-N, H-K, I-J, I-K).
    // Invariant pinned in smoke: PERM_PORTAL_MIN === PERMANENT_PORTALS.length.
    PERM_PORTAL_MIN: 4, // all permanent portals active for Final Boss
};
/** The 4 permanent portals (all bidirectional). Data Model §1.4. */
export const PERMANENT_PORTALS = ['A-N', 'H-K', 'I-J', 'I-K'];
/** Conditional portals (bidirectional, topic-triggered). Data Model §1.4. */
export const CONDITIONAL_PORTALS = [
    'B-L',
    'B-J',
    'C-Q',
    'C-O',
    'E-N',
    'F-N',
    'G-P',
    'H-P',
];
/** Simulated exam fixed grade boundaries (Data Model §1.6). */
export const GRADE_BOUNDARIES = {
    'A*': 90,
    A: 80,
    B: 70,
    C: 60,
    D: 50,
    E: 40,
    U: 0,
};
export const EXAM = {
    TIME_LIMIT_S: 5400, // 90 minutes
    MAX_SCORE: 80,
};
/**
 * Directional prerequisite chain (Phase 3.1 gating). Source of truth = dag.json
 * `prereqs_to_unlock`. A cluster unlocks only once EVERY listed prereq is CLEARED.
 * Entry clusters A (World 1) and J (World 2) have no prereqs — World 2 opens
 * independently of World 1 per the frozen PM decision.
 */
export const PREREQS = {
    A: [],
    B: ['A'],
    C: ['B'],
    D: ['B'],
    E: ['D'],
    F: ['B', 'C', 'D'],
    G: ['F'],
    H: ['G'],
    I: ['H'],
    J: [],
    K: ['J'],
    L: ['K'],
    M: ['L'],
    N: ['K', 'L'],
    O: ['N'],
    P: ['O'],
    Q: ['P', 'M'],
};
/** World 1 cluster ids (A..I). */
export const WORLD1_CLUSTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
/** World 2 cluster ids (J..Q). */
export const WORLD2_CLUSTERS = ['J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q'];
/**
 * Admin-knob defaults for the clear-predicate + portal pass bars (Phase 3.1).
 * Overridable at runtime via feature_flag_overrides; the app works with ZERO
 * override rows because these defaults always apply when no override is present.
 */
export const CLEAR_PREDICATE_DEFAULTS = {
    CLEAR_REGULAR_PCT: 0.8, // cluster "cleared" needs >= this fraction of regular battles cleared
    CLEAR_REQUIRE_BOSS: false, // when true, ALSO require stageboss >= STAGE_CLEAR_MIN_SCORE
    STAGE_CLEAR_MIN_SCORE: 5,
    // Portal pass bars (admin knobs) — portal "completed" = passed >= bar for its tier.
    PORTAL_PASS_6: 4, // 6-mark path bar (>=4/6)
    // M9-patch2: corrected from 5 → 6 to align with the 60% pass floor used by
    // the grader (Math.ceil(0.6 × 9) = 6) and the historical SQL queries (CEIL
    // (0.6 * mark_value)). Previously the knob (5) and the formula (6) diverged
    // only for 9-mark, producing inconsistent "cleared" vs "passBar" UI.
    // Invariant pinned in smoke: PORTAL_PASS_9 === Math.ceil(0.6 * 9).
    PORTAL_PASS_9: 6, // 9-mark conditional bar (>=6/9, 60% floor)
    PORTAL_PASS_12: 8, // 12-mark permanent bar (>=8/12)
};
//# sourceMappingURL=constants.js.map